import { ComboboxDropDownButton } from './combobox/ComboboxDropDownButton';
import { ComboboxInput } from './combobox/ComboboxInput';
import {
  type LegacyRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  ValidationResult,
  type ValidatorFn,
} from '@funnellab/shared/src/validation';
import ComboboxMultiple, {
  type ComboboxMultipleProperties,
} from '@components/input/ComboboxMultiple.tsx';

export type ComboboxProperties<Option> = {
  onChange: (newValue: Option | null) => void;
  value: Option | null;
  /** @deprecated validate the input on your own and pass the result */
  validate?: ValidatorFn<Option | null>;
  readonly children?: {
    options?: JSX.Element;
    error?: JSX.Element;
  };
} & Omit<ComboboxMultipleProperties<Option>, 'onChange' | 'validate' | 'value'>;

/**
 * @deprecated use new Combobox in `@components/base`
 */
export default forwardRef(function Combobox<T>(
  {
    onChange,
    value,
    validate,
    leadingIcon,
    showDropdownButton = false,
    displayValue,
    placeholder,
    required,
    options = [],
    sortFn,
    disableSort,
    onQueryChange,
    children,
    onBlur,
    ...rest
  }: ComboboxProperties<T>,
  ref: LegacyRef<HTMLInputElement>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(value ? displayValue(value) : '');
  const [showAllOptions, setShowAllOtions] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowAllOtions(false);
    }
  }, [isOpen]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setQuery(value ? displayValue(value) : ''), [value]);

  const onChangeInternal = useCallback(
    (newValue: T[]) => {
      const formattedValue = newValue.at(-1) ?? null;
      onChange(formattedValue);
      setQuery(formattedValue ? displayValue(formattedValue) : '');
    },
    [onChange, setQuery, displayValue],
  );

  const validateInternal = useCallback(
    (value: T[]) => {
      if (validate) {
        if (required && value.length === 0) {
          return ValidationResult.EMPTY;
        }

        return value.reduce((acc, val) => {
          const result = validate(val);
          if (result !== ValidationResult.VALID) {
            return result;
          } else {
            return acc;
          }
        }, ValidationResult.VALID);
      } else {
        return ValidationResult.VALID;
      }
    },
    [required, validate],
  );

  return (
    <ComboboxMultiple
      options={options}
      sortFn={sortFn}
      disableSort={disableSort}
      onChange={(value) => {
        onChangeInternal(value);
        setIsOpen(false);
      }}
      validate={validateInternal}
      value={value != null ? [value] : []}
      leadingIcon={leadingIcon}
      showDropdownButton={showDropdownButton}
      hideCheckmark={true}
      displayValue={displayValue}
      required={required}
      {...rest}
      classNameLabel={rest.classNameLabel}
      isOpen={isOpen}
      query={query}
      showAllOptions={showAllOptions}
      onBlur={() => {
        onBlur?.();
        setIsOpen(false);
        setQuery(value ? displayValue(value) : '');
      }}
      showSelectedOptions={true}
    >
      {{
        input: (
          <ComboboxInput<T>
            displayValue={displayValue}
            testId={rest.inputTestId}
            value={query}
            disabled={rest.disabled}
            placeholder={placeholder}
            onBlur={onBlur}
            className={rest.classNameInput}
            hasLeadingIcon={!!leadingIcon}
            onChange={(event) => {
              setShowAllOtions(false);
              const newQuery = (event.target as HTMLInputElement).value;
              setQuery(newQuery);
              setIsOpen(newQuery.length > 0);
              onQueryChange?.(newQuery);
            }}
            ref={ref}
          />
        ),
        dropDownButton: (
          <ComboboxDropDownButton<T>
            showDropdownButton={showDropdownButton}
            buttonTestId={rest.buttonTestId}
            isOpen={isOpen}
            setIsOpen={(isOpen) => {
              setIsOpen(isOpen);
              if (isOpen) {
                setShowAllOtions(true);
              }
            }}
          />
        ),
        ...children,
      }}
    </ComboboxMultiple>
  );
});
